import { Button, Input } from '../components/admin/ui';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, X, Mail, MessageSquare, Clock } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { adminApi, Profile } from '../lib/admin-api';
import { DataTable } from '../components/admin/DataTable';

const roleOptions = [
  { value: 'all', label: 'All Roles' },
  { value: 'customer', label: 'Customer' },
  { value: 'employee', label: 'Employee' },
  { value: 'agent', label: 'Agent' },
  { value: 'supervisor', label: 'Supervisor' },
  { value: 'admin', label: 'Admin' },
];

export function ContactsPage() {
  const { profile } = useAuth();
  const navigate = useNavigate();
  const [contacts, setContacts] = useState<(Profile & { conversation_count?: number })[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');

  useEffect(() => {
    async function fetchData() {
      if (!profile?.organization_id) return;
      setLoading(true);
      const data = await adminApi.getContacts(profile.organization_id, {
        search: searchQuery || undefined,
        role: roleFilter !== 'all' ? roleFilter : undefined,
      });
      setContacts(data);
      setLoading(false);
    }

    const debounce = setTimeout(fetchData, searchQuery ? 300 : 0);
    return () => clearTimeout(debounce);
  }, [profile?.organization_id, searchQuery, roleFilter]);

  const getRoleBadge = (role: string) => {
    const styles: Record<string, string> = {
      customer: 'bg-muted text-foreground',
      employee: 'bg-info text-info-foreground',
      agent: 'bg-primary/15 text-link',
      supervisor: 'bg-info text-info-foreground',
      admin: 'bg-warning text-warning-foreground',
      owner: 'bg-destructive/10 text-error-text',
    };
    return (
      <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${styles[role] || styles.customer}`}>
        {role}
      </span>
    );
  };

  const columns = [
    {
      key: 'name',
      header: 'Contact',
      render: (contact: Profile & { conversation_count?: number }) => (
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-primary/15 rounded-full flex items-center justify-center flex-shrink-0">
            <span className="text-sm font-medium text-link">
              {(contact.display_name || contact.email || '?')[0].toUpperCase()}
            </span>
          </div>
          <div>
            <div className="font-medium text-foreground">
              {contact.display_name || 'Unknown'}
            </div>
            {contact.email && (
              <div className="flex items-center gap-1 text-xs text-muted-foreground">
                <Mail className="w-3 h-3" />
                {contact.email}
              </div>
            )}
          </div>
        </div>
      ),
    },
    {
      key: 'role',
      header: 'Role',
      render: (contact: Profile) => getRoleBadge(contact.role),
    },
    {
      key: 'department',
      header: 'Department',
      render: (contact: Profile) => (
        <span className="text-muted-foreground">{contact.department || '—'}</span>
      ),
    },
    {
      key: 'conversations',
      header: 'Conversations',
      render: (contact: Profile & { conversation_count?: number }) => (
        <span className="inline-flex items-center gap-1 text-muted-foreground">
          <MessageSquare className="w-3.5 h-3.5" />
          {contact.conversation_count ?? '—'}
        </span>
      ),
    },
    {
      key: 'last_seen',
      header: 'Last Seen',
      render: (contact: Profile) => (
        <span className="inline-flex items-center gap-1 text-muted-foreground text-sm">
          <Clock className="w-3.5 h-3.5" />
          {contact.last_seen_at
            ? new Date(contact.last_seen_at).toLocaleDateString()
            : 'Never'}
        </span>
      ),
    },
    {
      key: 'created_at',
      header: 'Joined',
      render: (contact: Profile) => (
        <span className="text-muted-foreground text-sm">
          {new Date(contact.created_at).toLocaleDateString()}
        </span>
      ),
    },
  ];

  return (
    <div className="support-page">
      <div>
        <h1 className="support-page-title">Contacts</h1>
        <p className="text-muted-foreground mt-1">All customers and team members</p>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4 bg-card p-4 rounded-lg border border-border flex-wrap">
        {/* Search */}
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search by name or email..."
            className="w-full pl-9 pr-14 py-1.5 border border-border rounded-md text-sm focus:ring-2 focus:ring-ring focus:border-primary"
          />
          {searchQuery && (
            <Button aria-label="Clear search" size="iconSm" variant="ghost"
              onClick={() => setSearchQuery('')}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-muted-foreground"
            >
              <X className="w-4 h-4" />
            </Button>
          )}
        </div>

        {/* Role filter */}
        <div className="flex items-center gap-2">
          <label htmlFor="contactspage-1" className="text-sm font-medium text-foreground">Role:</label>
          <select id="contactspage-1"
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
            className="border border-border rounded-md px-3 py-1.5 text-sm focus:ring-2 focus:ring-ring focus:border-primary"
          >
            {roleOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        <span className="text-sm text-muted-foreground ml-auto">
          {contacts.length} contact{contacts.length === 1 ? '' : 's'}
        </span>
      </div>

      <DataTable
        columns={columns}
        data={contacts}
        loading={loading}
        emptyMessage="No contacts found"
        onRowClick={(contact) => navigate(`/contacts/${contact.id}`)}
        keyExtractor={(contact) => contact.id}
      />
    </div>
  );
}
